/**
 * Estado de sesión y de registro de quien está usando la aplicación.
 *
 * LECTURA ESTRICTA, A DIFERENCIA DE `cliente.ts`. Aquel archivo lee tolerante
 * porque se escribió cuando el contrato del backend todavía no existía. Aquí sí
 * existe: está en `docs/adr/0006-autenticacion.md` y en
 * `apps/api/src/rutas/perfil.ts`. Con el contrato escrito, aceptar formas
 * parecidas solo serviría para que una divergencia real pasara desapercibida y
 * apareciera como una pantalla a medias.
 *
 * `GET /api/perfil` DEVUELVE CUATRO FORMAS, Y NINGUNA ES UN ERROR:
 *
 *   { estado: 'anonimo',       autenticacion_disponible: false }
 *   { estado: 'anonimo',       autenticacion_disponible: true  }
 *   { estado: 'sin_registrar', correo, nombre }
 *   { estado: 'registrado',    usuario }
 *
 * `anonimo` con `autenticacion_disponible: false` significa que este servidor
 * NO tiene configuradas las variables de OAuth. Ahí no se pinta un botón de
 * «Entrar con Google»: llevaría a un 503. Es el estado del despliegue de hoy.
 *
 * `sin_registrar` es por donde pasa TODO alumno nuevo, exactamente una vez:
 * autenticado con Google, pero sin fila en `usuarios` porque Google no entrega
 * la matrícula (ADR-0006, decisión 2). No es un fallo: es el camino al
 * formulario de registro.
 *
 * La ruta nunca exige sesión. Un 503 sí es un error de verdad: el esquema de
 * autenticación no está aplicado en la base.
 */

import {
  ErrorApi,
  ErrorDeFormato,
  describirFalloDeRed,
  esRegistro,
  leerJson,
  mensajeDelCuerpo,
} from './errores'

const TIEMPO_LIMITE_MS = 5000
const TIEMPO_LIMITE_ESCRITURA_MS = 10000

/** Fila de `usuarios`: quién es esta persona dentro de la escuela. */
export interface UsuarioRegistrado {
  id: string
  correo: string
  /**
   * `null` para quien no tiene matrícula —mostrador y dirección—, nunca cadena
   * vacía. Se muestra como «sin dato»: es un hueco declarado, no un valor.
   */
  matricula: string | null
  telefono: string | null
  /**
   * Se MUESTRA, no se usa para decidir. Desde el ADR-0007 los roles son datos
   * y se pueden crear desde la propia aplicación, así que cualquier `if` que
   * compare contra un nombre concreto se queda obsoleto en cuanto alguien crea
   * un rol. Para decidir están los permisos.
   */
  rol: string
  /**
   * Lo que esta persona puede hacer, ya resuelto por el servidor.
   *
   * SIRVE PARA NO OFRECER LO QUE VA A REBOTAR, no para autorizar. Un array
   * vacío es legítimo —el rol `consulta` no tiene ninguno— y no significa que
   * la respuesta viniera mal.
   *
   * Se lee tolerante a su ausencia, y solo este campo: un servidor anterior a
   * este cambio no lo manda, y la aplicación debe degradarse a «no puede nada»
   * en vez de romperse. Es más seguro que suponer lo contrario.
   */
  permisos: string[]
}

export type Perfil =
  | { estado: 'anonimo'; autenticacion_disponible: boolean }
  | { estado: 'sin_registrar'; correo: string; nombre: string | null }
  | { estado: 'registrado'; usuario: UsuarioRegistrado }

/** Los dos estados en los que hay una sesión de Google detrás. */
export type EstadoConSesion = 'sin_registrar' | 'registrado'

export interface DatosDeRegistro {
  matricula: string
  telefono: string
}

/* --- Consultas ------------------------------------------------------------ */

/** `GET /api/perfil`. Lanza `ErrorApi` con el 503 del servidor, si lo hay. */
export async function obtenerPerfil(): Promise<Perfil> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/perfil', {
      headers: { accept: 'application/json' },
      // Las cookies de sesión de better-auth son de este mismo origen: el
      // navegador llega por BASE_URL y `/api` se sirve detrás (ADR-0006).
      credentials: 'same-origin',
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo contactar al servidor (${describirFalloDeRed(error)}).`,
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      mensajeDelCuerpo(cuerpo) ??
        `El servidor respondió ${respuesta.status} en /api/perfil.`,
    )
  }

  return interpretarPerfil(cuerpo)
}

/**
 * `POST /api/registro`. Devuelve el usuario recién creado.
 *
 * Los 400, 401, 409 y 503 salen como `ErrorApi` con el mensaje del servidor
 * intacto: ya viene redactado para leerse.
 */
export async function registrar(
  datos: DatosDeRegistro,
): Promise<UsuarioRegistrado> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/registro', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(datos),
      signal: AbortSignal.timeout(TIEMPO_LIMITE_ESCRITURA_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo enviar el registro (${describirFalloDeRed(error)}). ` +
        'Nada se guardó; puedes intentarlo de nuevo.',
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      mensajeDelCuerpo(cuerpo) ??
        `El servidor respondió ${respuesta.status} en /api/registro.`,
    )
  }

  if (!esRegistro(cuerpo)) {
    throw new ErrorDeFormato(
      'El registro se aceptó, pero el servidor no devolvió el usuario.',
    )
  }

  return interpretarUsuario(cuerpo['usuario'])
}

/* --- Interpretación del contrato ------------------------------------------ */

function interpretarPerfil(cuerpo: unknown): Perfil {
  if (!esRegistro(cuerpo)) {
    throw new ErrorDeFormato(
      'La respuesta de /api/perfil no es un objeto JSON.',
    )
  }

  const estado = cuerpo['estado']

  if (estado === 'anonimo') {
    const disponible = cuerpo['autenticacion_disponible']
    if (typeof disponible !== 'boolean') {
      throw new ErrorDeFormato(
        'El perfil anónimo llegó sin `autenticacion_disponible`, y de ese ' +
          'dato depende si se puede iniciar sesión en este servidor.',
      )
    }
    return { estado: 'anonimo', autenticacion_disponible: disponible }
  }

  if (estado === 'sin_registrar') {
    return {
      estado: 'sin_registrar',
      correo: exigirTexto(cuerpo, 'correo', '/api/perfil'),
      // El nombre lo pone Google y puede faltar; el correo no.
      nombre: textoOpcional(cuerpo, 'nombre'),
    }
  }

  if (estado === 'registrado') {
    return { estado: 'registrado', usuario: interpretarUsuario(cuerpo['usuario']) }
  }

  throw new ErrorDeFormato(
    `El servidor devolvió un estado de perfil que este cliente no conoce: ${describir(estado)}.`,
  )
}

function interpretarUsuario(valor: unknown): UsuarioRegistrado {
  if (!esRegistro(valor)) {
    throw new ErrorDeFormato(
      'La respuesta trae el estado «registrado» pero sin los datos del usuario.',
    )
  }

  return {
    id: exigirTexto(valor, 'id', 'el usuario'),
    correo: exigirTexto(valor, 'correo', 'el usuario'),
    matricula: textoOpcional(valor, 'matricula'),
    telefono: textoOpcional(valor, 'telefono'),
    rol: exigirTexto(valor, 'rol', 'el usuario'),
    permisos: leerPermisos(valor),
  }
}

/**
 * Los permisos, leídos SIN romper si no vienen.
 *
 * Es la única concesión a la lectura tolerante de este archivo, y tiene razón:
 * un servidor anterior al ADR-0007 no manda el campo. Degradarse a «no puede
 * nada» esconde botones que quizá deberían verse; exigirlo dejaría la
 * aplicación en blanco. La primera falla se ve y se arregla; la segunda parece
 * una caída.
 *
 * Se filtra a cadenas: un elemento que no lo sea no puede coincidir con ningún
 * permiso, y dejarlo pasar solo serviría para que reventara más adelante.
 */
function leerPermisos(valor: Record<string, unknown>): string[] {
  const crudo = valor['permisos']
  if (!Array.isArray(crudo)) return []
  return crudo.filter((p): p is string => typeof p === 'string')
}

function exigirTexto(
  registro: Record<string, unknown>,
  clave: string,
  origen: string,
): string {
  const valor = registro[clave]
  if (typeof valor !== 'string' || valor.trim() === '') {
    throw new ErrorDeFormato(
      `${origen} llegó sin el campo \`${clave}\`, que el contrato declara obligatorio.`,
    )
  }
  return valor
}

/** `null` explícito y ausencia significan lo mismo: no hay dato. */
function textoOpcional(
  registro: Record<string, unknown>,
  clave: string,
): string | null {
  const valor = registro[clave]
  if (typeof valor === 'string' && valor.trim() !== '') return valor
  return null
}

function describir(valor: unknown): string {
  return typeof valor === 'string' ? `«${valor}»` : String(valor)
}
